<template>
  <ValidationProvider
    v-slot="{ errors }"
    :name="$props.label"
    :rules="$props.rules"
    class="base-checklist"
    tag="div"
  >
    <input v-model="modelValue" type="hidden" />

    <BaseSearchInput v-if="$props.searchable" v-model="search" class="mb-3" />

    <b-form-group v-bind="$attrs">
      <template v-slot:default="{ ariaDescribedby }">
        <BaseChecklistSkeleton>
          <b-form-checkbox-group
            v-if="!!optionItems.length"
            v-model="modelValue"
            :aria-describedby="ariaDescribedby"
            stacked
          >
            <slot :v-bind="{ options: optionItems }">
              <BaseCheckbox
                v-for="optionItem in optionItems"
                :key="`base-checklist-itema-${optionItem.value}`"
                :checked-value="optionItem.value"
                :disabled="optionItem.disabled || false"
                class="mb-2"
              >
                <span class="ml-2">
                  <slot name="item" v-bind="optionItem">
                    {{ optionItem.text }}
                  </slot>
                </span>
              </BaseCheckbox>
            </slot>
          </b-form-checkbox-group>

          <p v-else class="mb-0 small">
            <slot name="empty-message">No items found.</slot>
          </p>
        </BaseChecklistSkeleton>
      </template>

      <template v-slot:label>
        <slot name="label">{{ $props.label }}</slot>

        <BaseChecklistAllToggle v-if="!$props.single && !!optionItems.length" />
      </template>
    </b-form-group>

    <div v-if="!!errors.length" class="invalid-feedback d-block">
      {{ errors[0] }}
    </div>
  </ValidationProvider>
</template>

<script>
import {
  defineComponent,
  computed,
  provide,
  toRefs
} from '@vue/composition-api'
import { ValidationProvider } from 'vee-validate'

import { useFuseSearch } from '@composables'
import { BaseCheckbox, BaseSearchInput } from '@components/base'

import BaseChecklistSkeleton from './BaseChecklistSkeleton.vue'
import BaseChecklistAllToggle from './BaseChecklistAllToggle.vue'

export default defineComponent({
  name: 'BaseChecklist',
  components: {
    ValidationProvider,

    BaseCheckbox,
    BaseSearchInput,

    BaseChecklistSkeleton,
    BaseChecklistAllToggle
  },
  props: {
    options: {
      type: Array,
      required: true
    },
    value: {
      type: Array,
      default: () => []
    },
    label: {
      type: String,
      default: ''
    },
    searchable: {
      type: Boolean,
      default: true
    },
    searchKeys: {
      type: Array,
      default: () => []
    },
    loading: Boolean,
    single: Boolean,
    rules: String
  },
  emits: ['input'],
  setup(props, { emit }) {
    const { options, loading } = toRefs(props)

    const { search, results: optionItems } = useFuseSearch(options, {
      keys: props.searchKeys
    })

    const modelValue = computed({
      get: () => props.value,
      set: (newValue) => {
        if (props.single) {
          const lastValue = newValue[newValue.length - 1]

          emit('input', [lastValue])
        } else {
          emit('input', newValue)
        }
      }
    })

    const setModelValue = (newModelValue) => {
      if (props.single) {
        const lastValue = newModelValue[newModelValue.length - 1]

        emit('input', [lastValue])
      } else {
        emit('input', newModelValue)
      }
    }

    provide('base-checklist', {
      loading,

      modelValue,
      setModelValue,

      optionItems
    })

    return {
      modelValue,

      search,
      optionItems
    }
  }
})
</script>

<style scoped>
.form-group >>> div[role='group'] {
  max-height: 285px;
  overflow-y: auto;
}

.form-group >>> .col-form-label {
  display: flex;
  align-items: center;
  justify-content: space-between;
}

.form-group >>> .custom-control-input ~ .custom-control-label::before,
.form-group >>> .custom-control-input ~ .custom-control-label::after {
  top: 50%;
}

.form-group >>> .custom-control-input ~ .custom-control-label::before {
  transform: translateY(-50%);
}

.form-group >>> .custom-control-input ~ .custom-control-label::after {
  margin-top: -8px;
}
</style>
